import { Button, Card, Layout, List, Page } from "@shopify/polaris";
import type { GetServerSideProps, GetServerSidePropsResult, NextPage } from 'next'
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef } from "react";

import { LoadingSkeletonPage } from '../components/LoadingSkeletonPage';
import { StatusBadge } from '../components/StatusBadge';
import { useMerchant } from '../data-hooks/merchant';
import { useVerifyScopes } from "../data-hooks/scopes";

const Dashboard: NextPage<{ shop: string }> = ({ shop }) => {
  const router = useRouter();
  const { merchant, isLoading, error } = useMerchant(shop);
  const verifyScopes = useVerifyScopes(shop);
  const hasScopesBeenChecked = useRef(false);

  useEffect(() => {
    if (hasScopesBeenChecked.current) {
      return;
    }

    if (!window.top) {
      return;
    }

    if (!shop) {
      return;
    }

    verifyScopes.mutate().then();

    hasScopesBeenChecked.current = true;
  }, [shop, verifyScopes]);

  if (error) {
    return (
      <LoadingSkeletonPage/>
    );
  }

  if (isLoading || !merchant) {
    return (
      <LoadingSkeletonPage/>
    );
  }

  return (
    <Page>
      <Layout>
        <Layout.AnnotatedSection
          id="dashboard"
          title="Dashboard"
          description="This is the overall status of Econt Delivery integration."
        >
          <Card>
            <Card.Section title="Quick Links">
              Go to
              &nbsp;
              <a href="https://delivery.econt.com" rel="noopener noreferrer" target="_blank">
                Econt Delivery dashboard
              </a>
            </Card.Section>

            {verifyScopes.data && verifyScopes.data.shouldReAuth && (
              <Card.Section title="Reauthorize App">
                You need to reauthorize the app to get the latest scopes before you can continue. Click the button below
                to reauthorize.
                <br/>
                <br/>
                <Button
                  primary
                  disabled={verifyScopes.isLoading}
                  onClick={() => {
                    if (!window.top) {
                      return;
                    }

                    if (verifyScopes.isLoading) {
                      return;
                    }

                    window.top.location.href = `${process.env.NEXT_PUBLIC_APP_URL}/api/auth?${window.location.search}`;
                  }}
                >
                  Reauthenticate
                </Button>
              </Card.Section>
            )}

            <Card.Section title="Econt Delivery Integration Status - Econt Form">
              <List type="number">
                <List.Item>
                  Econt Delivery Store <StatusBadge success={merchant.econtConnected}/>
                </List.Item>

                <List.Item>
                  Orders Synchronization <StatusBadge success={merchant.hasOrderWebhook}/>
                </List.Item>
              </List>
            </Card.Section>
            <Card.Section title="Start setup">
              Go to <Link href={{ pathname: 'instructions-econt-form', query: router.query }}>Econt Form setup
              instructions</Link> and follow the guide
            </Card.Section>
            <Card.Section title="Econt Delivery Integration Status - Carrier Service">
              <List type="number">
                <List.Item>
                  Econt Delivery Store <StatusBadge success={merchant.econtConnected}/>
                </List.Item>

                <List.Item>
                  Carrier Service Shipping Rates <StatusBadge success={merchant.hasCarrierService}/>
                </List.Item>

                <List.Item>
                  Orders Synchronization <StatusBadge success={merchant.hasOrderWebhook}/>
                </List.Item>
              </List>
            </Card.Section>
            <Card.Section title="Start setup">
              Go to <Link href={{ pathname: 'instructions-carrier-service', query: router.query }}>Carrier Service
              setup instructions</Link> and follow the guide
            </Card.Section>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
  );
}

export const getServerSideProps: GetServerSideProps<{
  shop: string
}> = async (context): Promise<GetServerSidePropsResult<{ shop: string }>> => {
  if (context.res) {
    context.res.setHeader(
      'Content-Security-Policy',
      `frame-ancestors https://${context.query.shop} https://admin.shopify.com;`
    );
  }

  return {
    props: {
      shop: context.query.shop as string || ''
    }
  };
}

export default Dashboard;
